import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Copy, FilePlus2, KeyRound, Loader2, Mail, Printer, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FactureDocument, totaux } from "@/components/factures/FactureDocument";
import { fmtDateHeure } from "@/components/scans/common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { MESSAGERIES, choisirMessagerie, messageriePreferee, ouvrirMail } from "@/lib/mail";
import { peut } from "@/lib/permissions";
import { paysEnAnglais } from "@/lib/pays";

// Lien direct vers le document (le code y est inclus : le client n'a rien à saisir).
export const lienFacture = (f) => `${window.location.origin}/facture/${f.token}?code=${f.code}`;

export function mailFacture(f) {
  return {
    to: f.email || "",
    subject: "Facture vierge",
    // Lien seul sur sa ligne et entre < > : la messagerie le rend cliquable.
    body: `Bonjour,\n\nMerci de compléter la facture pro forma de votre envoi en suivant ce lien :\n\n<${lienFacture(f)}>\n\n`
      + `(Si le lien demande un code d'accès : ${f.code})\n\n`
      + "Remplissez tous les champs, ajoutez une ligne par article, puis cliquez sur « Valider le document et clôturer ». "
      + "Imprimez ensuite le document pour le signer à la main.\n\nCordialement",
  };
}

export function StatutFacture({ f }) {
  const c = f.statut === "cloturee" ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/40"
    : f.statut === "en_cours" ? "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30"
      : "bg-sky-500/15 text-sky-600 dark:text-sky-400 border-sky-500/30";
  return <span className={`inline-flex text-[11px] font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ${c}`}>{f.statut_label}</span>;
}

/** Message copié en HTML (lien cliquable) + texte : à coller dans Outlook, Gmail… */
async function copierMessage(f) {
  const lien = lienFacture(f);
  const html = `<p>Bonjour,</p><p>Merci de compléter la facture pro forma de votre envoi en suivant ce lien :</p>`
    + `<p><a href="${lien}">Ouvrir la facture pro forma</a></p><p>(Si le lien demande un code d'accès : <b>${f.code}</b>)</p>`
    + `<p>Remplissez tous les champs, ajoutez une ligne par article, puis cliquez sur « Valider le document et clôturer ». Imprimez ensuite le document pour le signer à la main.</p><p>Cordialement</p>`;
  const texte = mailFacture(f).body;
  try {
    if (window.ClipboardItem) {
      await navigator.clipboard.write([new window.ClipboardItem({ "text/html": new Blob([html], { type: "text/html" }), "text/plain": new Blob([texte], { type: "text/plain" }) })]);
    } else {
      await navigator.clipboard.writeText(texte);
    }
    toast.success("Message copié : collez-le dans votre e-mail (Ctrl+V)");
  } catch { toast.error("Copie impossible"); }
}

const copier = (txt, quoi) => navigator.clipboard?.writeText(txt).then(() => toast.success(`${quoi} copié`), () => toast.error("Copie impossible"));

/** Lien + code + envoi par e-mail (messagerie au choix, mémorisée). */
export function EnvoiFacture({ f }) {
  const [mode, setMode] = useState(messageriePreferee());
  return (
    <div className="space-y-3" data-testid="envoi-facture">
      <div className="rounded-lg border border-border p-3 space-y-2">
        <div>
          <p className="text-xs text-muted-foreground">Lien de la facture vierge</p>
          <div className="flex gap-2 items-center">
            <code className="text-xs break-all flex-1" data-testid="facture-lien">{lienFacture(f)}</code>
            <Button size="sm" variant="ghost" onClick={() => copier(lienFacture(f), "Lien")} title="Copier le lien"><Copy size={14} /></Button>
          </div>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Code d'accès</p>
          <div className="flex gap-2 items-center">
            <span className="font-mono text-2xl font-extrabold tracking-[0.3em]" data-testid="facture-code">{f.code}</span>
            <Button size="sm" variant="ghost" onClick={() => copier(f.code, "Code")} title="Copier le code"><Copy size={14} /></Button>
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 items-end">
        <div className="space-y-1">
          <Label className="text-xs">Envoyer avec</Label>
          <select value={mode} onChange={(e) => { setMode(e.target.value); choisirMessagerie(e.target.value); }}
            className="h-9 rounded-md border border-input bg-transparent px-2 text-sm" data-testid="facture-messagerie">
            {MESSAGERIES.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
        </div>
        <Button onClick={() => ouvrirMail(mailFacture(f), mode)} data-testid="facture-envoyer"><Mail size={15} /> Envoyer par mail</Button>
        <Button variant="outline" onClick={() => copierMessage(f)} title="Copie le message avec un vrai lien cliquable, à coller dans l'e-mail" data-testid="facture-copier-message">
          <Copy size={15} /> Copier le message
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">L'e-mail est prérempli (objet « Facture vierge », lien direct vers le document). Si votre messagerie n'affiche pas le lien en bleu, utilisez « Copier le message » et collez-le dans l'e-mail.</p>
    </div>
  );
}

/** Onglet « Génération facture inter » : liste des factures pro forma. */
export default function FacturesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [liste, setListe] = useState(null);
  const [nouvelle, setNouvelle] = useState(null); // { libelle, email } puis facture créée
  const [creee, setCreee] = useState(null);
  const [busy, setBusy] = useState(false);
  const peutCreer = peut(user, "factures_creer");

  const charger = useCallback(async () => {
    try { setListe((await api.get("/factures-inter")).data); } catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const generer = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/factures-inter", nouvelle);
      setCreee(data); setNouvelle(null); charger();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <AppShell title="Génération facture inter">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Génération facture inter</h1>
          <p className="text-sm text-muted-foreground">Factures pro forma à faire remplir par le client (lien + code d'accès), puis à imprimer pour signature.</p>
        </div>
        {peutCreer && <Button onClick={() => setNouvelle({ libelle: "", email: "" })} data-testid="btn-generer-facture"><FilePlus2 size={15} /> Générer une facture vierge</Button>}
      </div>

      <div className="rounded-xl border border-border bg-card overflow-x-auto">
        <Table data-testid="factures-liste">
          <TableHeader>
            <TableRow>
              <TableHead>Créée le</TableHead><TableHead>Repère</TableHead><TableHead>Expéditeur → destinataire</TableHead>
              <TableHead>Lignes</TableHead><TableHead>Valeur</TableHead><TableHead>Statut</TableHead><TableHead>Code</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!liste ? <TableRow><TableCell colSpan={7} className="text-sm text-muted-foreground">Chargement…</TableCell></TableRow> : liste.map((f) => {
              const c = f.champs || {};
              const t = totaux(c.lignes || []);
              return (
                <TableRow key={f.id} className="cursor-pointer" onClick={() => navigate(`/factures/${f.id}`)} data-testid={`facture-${f.id}`}>
                  <TableCell className="text-sm whitespace-nowrap">{fmtDateHeure(f.created_at)}<span className="block text-xs text-muted-foreground">{f.created_by?.nom}</span></TableCell>
                  <TableCell className="text-sm">{f.libelle || "—"}{f.email && <span className="block text-xs text-muted-foreground">{f.email}</span>}</TableCell>
                  <TableCell className="text-xs max-w-[280px]">
                    {c.expediteur || c.destinataire
                      ? <><span className="font-medium">{(c.expediteur || "—").split("\n")[0]}</span> → <span className="font-medium">{(c.destinataire || "—").split("\n")[0]}</span>{c.pays_destination ? <span className="text-muted-foreground"> ({paysEnAnglais(c.pays_destination)})</span> : null}</>
                      : <span className="text-muted-foreground">Pas encore remplie</span>}
                  </TableCell>
                  <TableCell className="text-sm">{(c.lignes || []).length || "—"}</TableCell>
                  <TableCell className="text-sm whitespace-nowrap">{t.valeur ? `${t.valeur.toLocaleString("fr-FR", { minimumFractionDigits: 2 })} ${c.devise || "EUR"}` : "—"}</TableCell>
                  <TableCell><StatutFacture f={f} /></TableCell>
                  <TableCell className="font-mono text-sm">{f.code}</TableCell>
                </TableRow>
              );
            })}
            {liste && !liste.length && <TableRow><TableCell colSpan={7} className="text-sm text-muted-foreground">Aucune facture pour l'instant.{peutCreer && " Cliquez sur « Générer une facture vierge »."}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!nouvelle} onOpenChange={(o) => !o && !busy && setNouvelle(null)}>
        <DialogContent className="sm:max-w-md">
          {nouvelle && (
            <form onSubmit={generer} className="space-y-4">
              <DialogHeader>
                <DialogTitle>Générer une facture vierge</DialogTitle>
                <DialogDescription>Un lien et un code d'accès sont créés : le client remplit la facture lui-même.</DialogDescription>
              </DialogHeader>
              <div className="space-y-1.5"><Label>Repère (facultatif)</Label><Input value={nouvelle.libelle} onChange={(e) => setNouvelle({ ...nouvelle, libelle: e.target.value })} placeholder="ex. nom du client, n° de colis" data-testid="facture-libelle" /></div>
              <div className="space-y-1.5"><Label>E-mail du client (facultatif)</Label><Input type="email" value={nouvelle.email} onChange={(e) => setNouvelle({ ...nouvelle, email: e.target.value })} placeholder="client@exemple.fr" data-testid="facture-email" /></div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setNouvelle(null)}>Annuler</Button>
                <Button type="submit" disabled={busy} data-testid="facture-generer">{busy && <Loader2 size={14} className="animate-spin" />} Générer</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!creee} onOpenChange={(o) => !o && setCreee(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="facture-creee">
          <DialogHeader>
            <DialogTitle>Facture vierge générée</DialogTitle>
            <DialogDescription>Donnez le lien et le code d'accès au client, ou envoyez-les par e-mail.</DialogDescription>
          </DialogHeader>
          {creee && <EnvoiFacture f={creee} />}
          <DialogFooter><Button variant="outline" onClick={() => setCreee(null)}>Fermer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

/** Une facture : document rempli, impression, dé-clôture. */
export function FactureVoirPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState(null);
  const [confirmDel, setConfirmDel] = useState(false);

  const charger = useCallback(async () => {
    try { setF((await api.get(`/factures-inter/${id}`)).data); } catch (e) { toast.error(errMsg(e)); }
  }, [id]);
  useEffect(() => { charger(); }, [charger]);

  const action = async (url, msg) => {
    try { setF((await api.post(url)).data); toast.success(msg); } catch (e) { toast.error(errMsg(e)); }
  };
  const supprimer = async () => {
    try { await api.delete(`/factures-inter/${id}`); toast.success("Facture supprimée"); navigate("/factures"); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <AppShell title="Facture pro forma">
      <div className="print:hidden">
        <Link to="/factures" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4"><ArrowLeft size={15} /> Toutes les factures</Link>
        {f && (
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div>
              <h1 className="font-heading text-xl font-bold flex items-center gap-2">Facture pro forma {f.libelle && <span className="text-muted-foreground font-normal">· {f.libelle}</span>} <StatutFacture f={f} /></h1>
              <p className="text-sm text-muted-foreground">Créée le {fmtDateHeure(f.created_at)} par {f.created_by?.nom || "—"}{f.cloturee_le ? ` · validée par le client le ${fmtDateHeure(f.cloturee_le)}` : ""}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => window.print()} data-testid="facture-imprimer"><Printer size={15} /> Imprimer</Button>
              {f.statut === "cloturee" && peut(user, "factures_decloturer") && (
                <Button variant="outline" onClick={() => action(`/factures-inter/${id}/decloturer`, "Facture dé-clôturée : le client peut la modifier")} data-testid="facture-decloturer">
                  <RotateCcw size={15} /> Dé-clôturer
                </Button>
              )}
              {peut(user, "factures_creer") && (
                <Button variant="ghost" onClick={() => action(`/factures-inter/${id}/nouveau-code`, "Nouveau code d'accès créé")} title="L'ancien code ne fonctionnera plus"><KeyRound size={15} /> Nouveau code</Button>
              )}
              {peut(user, "factures_supprimer") && (confirmDel
                ? <Button variant="destructive" onClick={supprimer}>Supprimer ?</Button>
                : <Button variant="ghost" className="text-destructive" onClick={() => setConfirmDel(true)} title="Supprimer"><Trash2 size={15} /></Button>)}
            </div>
          </div>
        )}
        {f && f.statut !== "cloturee" && (
          <div className="mb-5 grid gap-4 lg:grid-cols-[1fr_1fr]">
            <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
              {f.statut === "vierge" ? "Le client n'a pas encore ouvert ou rempli la facture." : "Le client est en train de remplir la facture : elle n'est pas encore validée."}
            </div>
            {peut(user, "factures_creer") && <div className="rounded-xl border border-border bg-card p-3"><EnvoiFacture f={f} /></div>}
          </div>
        )}
      </div>
      {!f ? <p className="text-sm text-muted-foreground">Chargement…</p> : <FactureDocument champs={f.champs} date={f.date_document} />}
    </AppShell>
  );
}
